<template>
  <div class="app-home">
    <section class="app-home__hero">
      <LogoTile :branding="branding" :alt="tenantName" :size="44" />
      <div class="app-home__hero-text">
        <p v-if="greeting" class="app-home__greeting">{{ greeting }}</p>
        <h1 class="app-home__title">{{ welcomeTitle }}</h1>
        <p v-if="welcomeText" class="app-home__subtitle">{{ welcomeText }}</p>
      </div>
    </section>

    <div v-if="visibleTiles.length" class="app-home__grid">
      <button
        v-for="tile in visibleTiles"
        :key="tile.route"
        type="button"
        class="app-home__tile"
        @click="onTileClick(tile)"
      >
        <span class="app-home__tile-icon"><v-icon size="20">{{ tile.icon }}</v-icon></span>
        <span class="app-home__tile-title">{{ tile.title }}</span>
        <span v-if="tile.description" class="app-home__tile-desc">{{ tile.description }}</span>
        <v-icon size="16" class="app-home__tile-go">arrow_forward</v-icon>
      </button>
    </div>
    <div v-else class="app-home__empty-state">
      <v-icon size="26" class="app-home__empty-icon">apps</v-icon>
      <p class="app-home__empty">No apps are available yet.</p>
      <p class="app-home__empty-hint">An administrator can add modules to {{ tenantName }} from System Admin.</p>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { navigateToUrl } from "single-spa";
import { useSessionStore } from "@/stores/session";
import { isTileRoutePermitted } from "@/nav/routeGuard";
import LogoTile from "@/shell/LogoTile.vue";
import type { TenantHomeTile } from "@/tenant/types";

/**
 * Rendered at `/` when `tenant.homePage?.enabled` (App.vue), Appendix A7.
 * Tiles are pre-validated backend-side to sit under one of the app's module
 * prefixes (and inside `allowedItems` when set) — `isTileRoutePermitted`
 * re-checks that against the CALLER's actually-resolved modules, so a tile
 * configured before a module/allowedItems change was tightened never renders
 * a dead or now-forbidden link.
 */
export default defineComponent({
  name: "AppHome",
  components: { LogoTile },
  setup() {
    const session = useSessionStore();

    const tenantName = computed(() => session.tenant?.name || "Metaship");
    const branding = computed(() => session.tenant?.branding ?? null);

    const homePage = computed(() => session.tenant?.homePage ?? null);
    const welcomeTitle = computed(() => homePage.value?.welcomeTitle || tenantName.value);
    const welcomeText = computed(() => homePage.value?.welcomeText || null);

    const visibleTiles = computed<TenantHomeTile[]>(() => {
      const tiles = homePage.value?.tiles ?? [];
      const modules = session.tenantModules;
      return tiles.filter((tile) => isTileRoutePermitted(tile.route, modules));
    });

    function onTileClick(tile: TenantHomeTile): void {
      navigateToUrl(tile.route);
      const [path, query] = tile.route.split("?");
      window.dispatchEvent(
        new CustomEvent("metaship:navigate", {
          detail: { path, query: Object.fromEntries(new URLSearchParams(query || "")) },
        }),
      );
    }

    /** First name only — a full name reads as a form field, not a greeting. */
    const greeting = computed(() => {
      const first = session.user?.firstname?.trim();
      return first ? `Welcome back, ${first}` : null;
    });

    return { tenantName, branding, greeting, welcomeTitle, welcomeText, visibleTiles, onTileClick };
  },
});
</script>

<style scoped>
.app-home {
  max-width: 1040px;
  margin: 0 auto;
  padding: 28px 24px 40px;
}

.app-home__hero {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}
.app-home__greeting {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--sh-text-45);
  margin: 0 0 3px;
}
.app-home__title {
  font-size: 21px;
  font-weight: 700;
  letter-spacing: -0.015em;
  margin: 0;
  color: var(--sh-text);
}
.app-home__subtitle {
  font-size: 13px;
  color: var(--sh-text-60);
  margin: 4px 0 0;
}

.app-home__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(228px, 1fr));
  gap: 12px;
}

/* Card language mirrors legacy-client's `.infoCards`: hairline border on the
   card surface, a tinted icon tile, 13px/700 title, 12px muted description. */
.app-home__tile {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--sh-hairline);
  background: rgb(var(--v-theme-card));
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition:
    transform 120ms ease,
    box-shadow 120ms ease,
    border-color 120ms ease;
}
.app-home__tile:hover,
.app-home__tile:focus-visible {
  border-color: rgba(var(--v-theme-primary), 0.55);
  box-shadow: 0 10px 24px -14px rgba(0, 0, 0, 0.55);
  transform: translateY(-1px);
  outline: none;
}
.app-home__tile-icon {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  margin-bottom: 4px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgb(var(--v-theme-primary)), rgb(var(--v-theme-secondary)));
  color: #fff;
  box-shadow: 0 4px 12px -5px rgba(var(--v-theme-primary), 0.8);
}
.app-home__tile-icon :deep(.v-icon) {
  color: #fff !important;
}
.app-home__tile-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--sh-text);
}
.app-home__tile-desc {
  font-size: 12px;
  line-height: 1.45;
  color: var(--sh-text-60);
}
.app-home__tile-go {
  position: absolute;
  top: 16px;
  right: 14px;
  color: var(--sh-text-45) !important;
  opacity: 0;
  transition: opacity 120ms ease, transform 120ms ease;
}
.app-home__tile:hover .app-home__tile-go {
  opacity: 1;
  transform: translateX(2px);
}

.app-home__empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
  padding: 48px 24px;
  border-radius: 12px;
  border: 1px dashed var(--sh-hairline);
}
.app-home__empty-icon {
  color: var(--sh-text-45) !important;
  margin-bottom: 4px;
}
.app-home__empty {
  font-size: 13px;
  font-weight: 600;
  color: var(--sh-text);
  margin: 0;
}
.app-home__empty-hint {
  font-size: 12px;
  color: var(--sh-text-45);
  margin: 0;
}
</style>
